import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Row, Col, Spinner, Table } from 'react-bootstrap';
import {
  ListDesktop,
  ListMobile,
  MobileCard,
  MobileCardTop,
  MobileCardTitle,
  MobileCardMeta,
  MobileCardRow,
} from './ListMobileLayout';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarElement
} from 'chart.js';
import { fetchLogsDateChart, fetchLogsTypeChart, fetchLogs } from '../../redux/actions/logActions';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarElement
);

const ErrorLogs = () => {
  const dispatch = useDispatch();
  const dateChart = useSelector(state => state.log.dateChart);
  const typeChart = useSelector(state => state.log.typeChart);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const logs = useSelector(state => state.log.logs);

  useEffect(() => {
    dispatch(fetchLogsDateChart());
    dispatch(fetchLogsTypeChart());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchLogs(page, itemsPerPage));
  }, [dispatch, page, itemsPerPage]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    dispatch(fetchLogs(1, itemsPerPage));
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setPage(1);
  };

  const renderPaginationPages = () => {
    const totalPages = Math.ceil(logs.data.data.objects.total / itemsPerPage);
    const pages = [];

    if (totalPages <= 4) {
      // Show all pages if 4 or fewer
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first two pages
      pages.push(1);
      pages.push(2);
      
      // Add ellipsis
      pages.push('...');
      
      // Show second to last and last page
      pages.push(totalPages - 1);
      pages.push(totalPages);
    }

    return pages;
  };

  const logItems = logs.data?.success ? logs.data.data.items : [];
  const totalEntries = logs.data?.success ? logs.data.data.objects.total : 0;

  return (
    <div className="dashboard-list-page">
      <h2 className="mb-4">Error Logs</h2>
      <Row className="mb-4">
        <Col md={6}>
          <Card>
            <Card.Body>
              <Card.Title>Errors by Date</Card.Title>
              {dateChart.loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: 250 }}>
                  <Spinner animation="border" />
                </div>
              ) : dateChart.data && dateChart.data.success ? (
                <Line
                  data={{
                    labels: dateChart.data.data.map(item => item.date_string),
                    datasets: [
                      {
                        label: 'Errors',
                        data: dateChart.data.data.map(item => item.value),
                        fill: false,
                        borderColor: 'rgba(54, 162, 235, 1)',
                        backgroundColor: 'rgba(54, 162, 235, 0.2)',
                        tension: 0.3,
                        pointRadius: 3,
                        pointHoverRadius: 6,
                      }
                    ]
                  }}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: { display: false },
                      title: { display: false },
                      tooltip: { enabled: true }
                    },
                    scales: {
                      x: { title: { display: false } },
                      y: { beginAtZero: true, title: { display: false } }
                    }
                  }}
                  height={250}
                />
              ) : (
                <div style={{ height: 250 }} className="d-flex align-items-center justify-content-center text-muted">
                  No data
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card>
            <Card.Body>
              <Card.Title>Errors by Type</Card.Title>
              {typeChart.loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: 250 }}>
                  <Spinner animation="border" />
                </div>
              ) : typeChart.data && typeChart.data.success ? (
                <Bar
                  data={{
                    labels: typeChart.data.data.map(item => item.label),
                    datasets: [
                      {
                        label: 'Errors',
                        data: typeChart.data.data.map(item => item.value),
                        backgroundColor: 'rgba(255, 99, 132, 0.5)',
                        borderColor: 'rgba(255, 99, 132, 1)',
                        borderWidth: 1
                      }
                    ]
                  }}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: { display: false },
                      title: { display: false },
                      tooltip: { enabled: true }
                    },
                    scales: {
                      x: { title: { display: false } },
                      y: { beginAtZero: true, title: { display: false } }
                    }
                  }}
                  height={250}
                />
              ) : (
                <div style={{ height: 250 }} className="d-flex align-items-center justify-content-center text-muted">
                  No data
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Card>
        <Card.Body>
          <Card.Title>Error Details</Card.Title>
          <form className="d-flex flex-wrap gap-2 mb-3" onSubmit={handleSearch}>
            <input
              type="text"
              className="form-control flex-grow-1"
              placeholder="Search by error reason or code..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ minWidth: 0 }}
            />
            <button className="btn btn-primary" type="submit">Search</button>
            <select
              className="form-select"
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              style={{ width: 120, flex: '0 0 auto' }}
            >
              {[10, 20, 50].map(size => (
                <option key={size} value={size}>{size} per page</option>
              ))}
            </select>
          </form>
          {logs.loading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ height: 200 }}>
              <Spinner animation="border" />
            </div>
          ) : logs.data && logs.data.success ? (
            <div>
              <ListDesktop>
                <Table hover bordered className="mb-0">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Reason</th>
                      <th>Device Name</th>
                      <th>OS Version</th>
                      <th>App Version</th>
                      <th>Country Code</th>
                      <th>Created Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logItems.map(item => (
                      <tr key={item.id}>
                        <td>{item.id}</td>
                        <td>{item.reason?.message}</td>
                        <td>{item.device_info?.model}</td>
                        <td>{item.device_info?.version}</td>
                        <td>{item.app_version}</td>
                        <td>{item.country_code}</td>
                        <td>{item.created_at ? new Date(item.created_at).toLocaleString() : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </ListDesktop>

              <ListMobile>
                {logItems.map(item => (
                  <MobileCard key={item.id}>
                    <MobileCardTop>
                      <MobileCardTitle>#{item.id}</MobileCardTitle>
                    </MobileCardTop>
                    <MobileCardMeta>
                      {item.created_at ? new Date(item.created_at).toLocaleString() : '—'}
                    </MobileCardMeta>
                    <MobileCardRow label="Reason">
                      {item.reason?.message || '—'}
                    </MobileCardRow>
                    <MobileCardRow label="Device">
                      {item.device_info?.model || '—'}
                    </MobileCardRow>
                    <MobileCardRow label="OS Version">
                      {item.device_info?.version || '—'}
                    </MobileCardRow>
                    <MobileCardRow label="App Version">
                      {item.app_version || '—'}
                    </MobileCardRow>
                    <MobileCardRow label="Country">
                      {item.country_code || '—'}
                    </MobileCardRow>
                  </MobileCard>
                ))}
              </ListMobile>

              <div className="d-flex flex-column flex-md-row justify-content-between align-items-stretch align-items-md-center gap-2 mt-3">
                <div>
                  Showing {logItems.length} of {totalEntries} entries
                </div>
                <nav>
                  <ul className="pagination mb-0 flex-wrap">
                    <li className={`page-item${page === 1 ? ' disabled' : ''}`}>
                      <button className="page-link" onClick={() => handlePageChange(page - 1)} disabled={page === 1}>Previous</button>
                    </li>
                    {renderPaginationPages().map((pageNum, index) => (
                      <li
                        key={index}
                        className={`page-item${pageNum === page ? ' active' : ''}${pageNum === '...' ? ' disabled' : ''}`}
                      >
                        <button
                          className="page-link"
                          onClick={() => pageNum !== '...' && handlePageChange(pageNum)}
                          disabled={pageNum === '...'}
                        >
                          {pageNum}
                        </button>
                      </li>
                    ))}
                    <li className={`page-item${page === Math.ceil(totalEntries / itemsPerPage) ? ' disabled' : ''}`}>
                      <button className="page-link" onClick={() => handlePageChange(page + 1)} disabled={page === Math.ceil(totalEntries / itemsPerPage)}>Next</button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          ) : (
            <div style={{ height: 200 }} className="d-flex align-items-center justify-content-center text-muted">
              No data
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default ErrorLogs; 